import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup, School } from '../../../../../../api/groups';
import { SchoolYearValue, useResultsDataContext } from '../../ResultsDataContext';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import TABS from '../../../../../../shared-components/elementis/types/ResultsPageTabs';
import { groupBelongsToSelectedSchoolYear } from '../../../utils/groupBelongsToSelectedSchoolYear';

interface Opts {
  selectedSchoolYear: SchoolYearValue | null;
  selectedSchool: School | null;
  selectedTab: 'STUDENT' | 'EXERCISE';
}

export function useSelectedGroups({ selectedTab, selectedSchoolYear, selectedSchool }: Opts) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { groups } = useResultsDataContext();

  const selectedGroupIds = searchParams.get(FilterSearchParam.Groups);

  const selectedGroups = useMemo(() => {
    const selectedGroupIdsArray = selectedGroupIds?.split(',') || [];

    if (selectedTab === TABS.STUDENT) {
      const selectedGroup = groups.find(g => g.id === selectedGroupIdsArray[0]);
      if (selectedGroup) return [selectedGroup];
    } else {
      if (!selectedGroupIdsArray.length) {
        return groups.filter(
          g =>
            g.acceptedMemberCount > 0 &&
            groupBelongsToSelectedSchoolYear(g, selectedSchoolYear?.id) &&
            g.school.id === selectedSchool?.id,
        );
      }

      const selectedGroups = selectedGroupIdsArray
        .map(id => groups.find(g => g.id === id))
        .filter(Boolean) as CurrentOrPreviousGroup[];

      if (selectedGroups.length === selectedGroupIdsArray.length) return selectedGroups;
    }
    return [];
  }, [groups, selectedGroupIds, selectedSchool?.id, selectedSchoolYear?.id, selectedTab]);

  const syncSelectedGroupsWithSearchParams = useCallback(
    (groups: CurrentOrPreviousGroup[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        newParams.set(FilterSearchParam.Groups, groups.map(g => g.id).join(','));
        newParams.delete(FilterSearchParam.Members);
        return newParams;
      });
    },
    [setSearchParams],
  );

  return [selectedGroups, syncSelectedGroupsWithSearchParams] as const;
}
